import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Flex,
  IconButton,
  ModalProps,
  Container,
  Box,
  useDisclosure,
  UseDisclosureProps,
} from '@everlywell/ui-kit';
import { useLocation } from '@gatsbyjs/reach-router';
import DynamicIcon from 'components/DynamicIcon';
import Stepper from 'components/Stepper';
import { useAsyncProgramPPQContext } from 'contexts/AsyncProgramPPQ/AsyncProgramPPQContext';
import React, { ReactNode, useEffect } from 'react';

import { LAUNCH_HASH } from './constants';

export type PrePurchaseStepType = {
  mainContent: ReactNode;
  rightSideContent?: ReactNode;
  backgroundColor?: string;
};

export type AsyncProgramPPQModalProps = {
  initialScreenContent: ReactNode;
  steps: PrePurchaseStepType[];
  finalScreenContent: ReactNode;
  ineligibleScreenContent: ReactNode;
  modalSize?: ModalProps['size'];
} & UseDisclosureProps;

export default function AsyncProgramPPQModal({
  modalSize = 'full',
  initialScreenContent,
  steps,
  finalScreenContent,
  ineligibleScreenContent,
  ...modalProps
}: AsyncProgramPPQModalProps) {
  const {
    isModalOpen,
    toggleModal,
    prevStep,
    setNumberOfSteps,
    currentStep,
    isQuizStarted,
    isQuizEnded,
    isAnswerIneligible,
    areControlsVisible,
  } = useAsyncProgramPPQContext();

  // Chakra UI modal hook
  const { isOpen, onClose } = useDisclosure({
    ...modalProps,
    isOpen: isModalOpen,
  });

  const handleOnClose = () => {
    toggleModal(false);
    onClose();
  };

  // Set number of steps on mount
  useEffect(() => {
    setNumberOfSteps(steps.length);
  }, [setNumberOfSteps, steps.length]);

  const isQuizInProgress = isQuizStarted && !isQuizEnded && !isAnswerIneligible;

  const getNonQuizScreen = () => {
    if (!isQuizStarted) {
      return initialScreenContent;
    }

    if (isAnswerIneligible) {
      return ineligibleScreenContent;
    }

    if (isQuizEnded) {
      return finalScreenContent;
    }

    return null;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleOnClose}
      size={modalSize}
      autoFocus={false}
    >
      <ModalOverlay />
      <ModalContent
        // Padding to allow space for the sticky button
        paddingBottom={isQuizInProgress ? ['110px', null, 0] : undefined}
        backgroundColor={steps[currentStep]?.backgroundColor || undefined}
        containerProps={{
          // Set z-index on same level as navigation
          zIndex: 'tooltip',
        }}
      >
        <ModalBody
          // Remove padding on initial, final and ineligible screens
          padding={
            !isQuizStarted ||
            (isQuizStarted && isQuizEnded) ||
            isAnswerIneligible
              ? '0'
              : undefined
          }
        >
          <Box
            display={getNonQuizScreen() !== null ? 'block' : 'none'}
            height="100%"
          >
            {getNonQuizScreen()}
          </Box>

          <Box
            display={
              isQuizStarted && !isQuizEnded && !isAnswerIneligible
                ? 'flex'
                : 'none'
            }
            flexDirection="row"
            justifyContent="center"
            width="100%"
            height="auto"
            paddingY="4"
          >
            <Container
              size={['fluid', null, null, 'xl']}
              display="flex"
              flexDirection="column"
              margin="0 auto"
              height="100%"
            >
              <Flex
                flexDirection="column"
                width={['100%', null, '60%']}
                margin="0 auto"
              >
                {/* Steps indicators, back and close buttons */}
                <Flex gap="2" flexDirection="column" marginBottom="10">
                  {areControlsVisible ? (
                    <>
                      <Stepper
                        activeStepIndex={currentStep}
                        hideIndicators={true}
                        steps={Array.from({ length: steps.length }, (_, i) => ({
                          title: `${i + 1}`,
                        }))}
                        stepperTheme={{
                          base: 'viridian.base',
                          light: 'viridian.light',
                        }}
                      />
                      <Flex justifyContent="space-between">
                        <IconButton
                          colorScheme="whiteCS"
                          size="sm"
                          aria-label="previous-step"
                          icon={
                            <DynamicIcon
                              icon="ArrowLeft"
                              color="tints.uiGrey"
                            />
                          }
                          onClick={prevStep}
                          width="40px"
                          height="40px"
                        />
                        <IconButton
                          colorScheme="whiteCS"
                          size="sm"
                          aria-label="close"
                          icon={<DynamicIcon icon="X" color="tints.uiGrey" />}
                          onClick={handleOnClose}
                          width="40px"
                          height="40px"
                        />
                      </Flex>
                    </>
                  ) : null}
                </Flex>
                {/* Main content */}
                {steps?.map((step, index) => (
                  <Box
                    display={currentStep === index ? 'flex' : 'none'}
                    data-testid={`main-content-step-${index}`}
                    flexDirection="column"
                    key={`main-content-step-${index}`}
                    height="100%"
                  >
                    {step?.mainContent}
                  </Box>
                ))}
              </Flex>

              {/* Right side content */}
              {steps?.map(
                (step, index) =>
                  step.rightSideContent && (
                    <Box
                      width="40%"
                      display={[
                        'none',
                        null,
                        currentStep === index ? 'block' : 'none',
                      ]}
                      data-testid={`right-side-content-${index}`}
                      key={`right-side-content-${index}`}
                    >
                      {step.rightSideContent}
                    </Box>
                  ),
              )}
            </Container>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

// Use this wrapper to set the context for the modal to be available
export const AsyncPPQModalWrapper = (props: AsyncProgramPPQModalProps) => {
  const {
    setIsPrePurchaseQuizAvailable,
    toggleModal: toggleAsyncProgramPPQModal,
  } = useAsyncProgramPPQContext();
  const { hash } = useLocation();

  useEffect(() => {
    if (hash === LAUNCH_HASH) {
      toggleAsyncProgramPPQModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsPrePurchaseQuizAvailable(true);
    return () => setIsPrePurchaseQuizAvailable(false);
  }, [setIsPrePurchaseQuizAvailable]);

  return <AsyncProgramPPQModal {...props} />;
};
